'use client'
import { disableQuery, useQuery } from '@connectrpc/connect-query'
import { BookingStatus } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/booking_status_pb'
import {
  getBookingPublic,
  verifyCustomBooking,
} from '@fingertip/creator-proto/gen/fingertip/creator/booking/v1/booking-BookingService_connectquery'
import {
  getEventTypePublic,
  getEventTypeSchedule,
  getEventTypesPublic,
} from '@fingertip/creator-proto/gen/fingertip/creator/event_type/v1/event_type-EventTypeService_connectquery'
import { getGroupClassAttendancePublic } from '@fingertip/creator-proto/gen/fingertip/creator/group_class_attendance/v1/group_class_attendance-GroupClassAttendanceService_connectquery'
import { getSiteAvailabilityByDay } from '@fingertip/creator-proto/gen/fingertip/creator/nylas/v1/nylas-NylasService_connectquery'
import { getEventTypesScheduledMembersPublic } from '@fingertip/creator-proto/gen/fingertip/creator/schedule/v1/schedule-ScheduleService_connectquery'
import { getStoreConfigPublic } from '@fingertip/creator-proto/gen/fingertip/creator/store_config/v1/store_config-StoreConfigService_connectquery'
import { getUserProfile } from '@fingertip/creator-proto/gen/fingertip/creator/user_profile/v1/user_profile-UserProfileService_connectquery'
import { addDays, endOfMonth, format, startOfMonth } from 'date-fns'
import { observer } from 'mobx-react-lite'
import Link from 'next/link'
import { useRouter } from 'nextjs-toploader/app'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Booker } from '@/components/booking/booker/booker'
import { eventLocationToBookingLocationMapper } from '@/components/booking/mappers'
import { AlertBanner } from '@/components/shared/alert-banner'
import { Spinner } from '@/components/ui/spinner'
import { useBookingSelection } from '@/lib/hooks/use-booking-selection'
import { useToken } from '@/lib/hooks/use-token'
import { RootStoreContext } from '@/lib/stores/root-store'

type Props = {
  siteSlug: string
  source: string
  onRootBack?: () => void
  isEmbedMode?: boolean
}

export const BookerPage = observer(
  ({ siteSlug, source, onRootBack, isEmbedMode }: Props) => {
    const { t } = useTranslation()
    const { back } = useRouter()
    const { callOptions, token } = useToken()

    const {
      bookerStore: {
        month,
        bookingId,
        attendanceId,
        eventTypeId,
        selectedDate,
        bookingItemsData,
        setSelectedLocation,
        setStage,
        timeZone,
        selectedUserId,
        selectionToken,
        setCustomPrice,
        setCustomLength,
        setSelectedUserId,
        addBookingItem,
        customLength,
      },
    } = useContext(RootStoreContext)

    const bookingSelection = useBookingSelection(bookingItemsData)

    const eventTypes = useMemo(() => {
      return bookingItemsData.reduce((acc, item) => {
        return {
          ...acc,
          [item.eventTypeId]: item.quantity,
        }
      }, {})
    }, [bookingItemsData])

    const {
      data: eventTypesData,
      error: eventTypesError,
      isLoading: eventTypesIsLoading,
    } = useQuery(getEventTypesPublic, {
      siteSlug,
    })

    const {
      data: verifyCustomData,
      error: verifyCustomError,
      isLoading: verifyCustomIsLoading,
    } = useQuery(
      verifyCustomBooking,
      !selectionToken || !eventTypesData
        ? disableQuery
        : {
            siteSlug,
            selection: selectionToken,
          },
    )

    const {
      data: eventTypeData,
      error: eventTypeError,
      isLoading: eventTypeIsLoading,
    } = useQuery(
      getEventTypePublic,
      !eventTypeId
        ? disableQuery
        : {
            siteSlug,
            eventTypeId,
          },
    )

    const { data: membersData, error: membersError } = useQuery(
      getEventTypesScheduledMembersPublic,
      !siteSlug || bookingItemsData.length === 0
        ? disableQuery
        : {
            siteSlug,
            eventTypes,
          },
      {},
    )

    const { data: storeConfigData, isLoading: storeConfigLoading } = useQuery(
      getStoreConfigPublic,
      !siteSlug
        ? disableQuery
        : {
            siteSlug,
          },
      {},
    )

    const { data: userData } = useQuery(
      getUserProfile,
      !token ? disableQuery : {},
      {
        callOptions,
      },
    )

    const { data: scheduleData, error: scheduleError } = useQuery(
      getEventTypeSchedule,
      bookingItemsData.length === 0
        ? disableQuery
        : {
            siteSlug,
            eventTypes: eventTypes,
            rangeStart: format(startOfMonth(month), 'yyyy-MM-dd'),
            rangeEnd: format(addDays(endOfMonth(month), 1), 'yyyy-MM-dd'),
            clientTimeZone: timeZone,
            userId: selectedUserId ?? undefined,
            customLength: customLength ?? undefined,
          },
      {},
    )

    const {
      data: bookingData,
      error: bookingError,
      isLoading: bookingIsLoading,
    } = useQuery(
      getBookingPublic,
      !bookingId
        ? disableQuery
        : {
            bookingId: bookingId || undefined,
          },
      {},
    )

    const {
      data: attendanceData,
      error: attendanceError,
      isLoading: attendanceIsLoading,
    } = useQuery(
      getGroupClassAttendancePublic,
      !attendanceId
        ? disableQuery
        : {
            groupClassAttendanceId: attendanceId || undefined,
            siteSlug,
          },
      {},
    )

    const {
      data: availabilityData,
      error: availabilityError,
      isLoading: availabilityIsLoading,
    } = useQuery(
      getSiteAvailabilityByDay,
      !bookingItemsData.length || !siteSlug || !selectedDate
        ? disableQuery
        : {
            siteSlug,
            eventTypes: eventTypes,
            rangeStart: format(selectedDate, 'yyyy-MM-dd'),
            rangeEnd: format(addDays(selectedDate, 1), 'yyyy-MM-dd'),
            clientTimeZone: timeZone,
            userId: selectedUserId ?? undefined,
            customLength: customLength ?? undefined,
          },
      {},
    )

    // TODO: This section only happens because location is NOT selectable
    useEffect(() => {
      if (bookingItemsData.length === 0 || !bookingSelection) {
        setSelectedLocation(null)
      } else if (bookingItemsData.length === 1) {
        setSelectedLocation(
          eventLocationToBookingLocationMapper(
            bookingItemsData[0].eventType.locations[0],
          ),
        )
      } else {
        // Multiple locations
        setSelectedLocation(bookingSelection.bookingItemsLocation)
      }
    }, [bookingItemsData, bookingSelection, setSelectedLocation])

    useEffect(() => {
      if (verifyCustomData) {
        if (verifyCustomData.customPrice !== undefined) {
          setCustomPrice(verifyCustomData.customPrice)
        }

        if (verifyCustomData.customLength !== undefined) {
          setCustomLength(verifyCustomData.customLength)
        }

        setSelectedUserId(verifyCustomData.assigneeId)

        Object.entries(verifyCustomData.eventTypes).forEach(
          ([id, quantity]) => {
            const eventType = eventTypesData?.eventTypes.find(
              (eventType) => eventType.id === id,
            )
            if (eventType) {
              addBookingItem(eventType, quantity)
            }
          },
        )

        setStage('DATE_PICKER')
      }
    }, [
      addBookingItem,
      eventTypesData?.eventTypes,
      setCustomLength,
      setCustomPrice,
      setSelectedUserId,
      setStage,
      verifyCustomData,
    ])

    const rootBackCallback = useCallback(() => {
      if (onRootBack) {
        return onRootBack()
      } else if (bookingData?.booking?.status === BookingStatus.ACCEPTED) {
        return setStage('SUCCESS')
      } else {
        return back()
      }
    }, [back, bookingData?.booking?.status, onRootBack, setStage])

    const error =
      eventTypesError ||
      eventTypeError ||
      scheduleError ||
      bookingError ||
      availabilityError ||
      attendanceError ||
      membersError

    if (error) {
      return (
        <div className="booker-container">
          <AlertBanner>{error.message}</AlertBanner>
        </div>
      )
    }

    if (verifyCustomError) {
      return (
        <div className="booker-container">
          <AlertBanner
            variant="destructive"
            title={t('Booking link expired or invalid')}
            className="flex flex-col items-center gap-4"
          >
            <p>
              {t(
                'The booking link you are trying to use is no longer valid. Please request a new link.',
              )}
            </p>
            <br />
            <Link
              className="underline text-destructive-foreground"
              href={`/${siteSlug}`}
            >
              {t('common:go_back')}
            </Link>
          </AlertBanner>
        </div>
      )
    }

    if (
      eventTypesIsLoading ||
      eventTypeIsLoading ||
      bookingIsLoading ||
      attendanceIsLoading ||
      storeConfigLoading ||
      verifyCustomIsLoading ||
      !storeConfigData?.config
    ) {
      return (
        <div className="mx-auto w-full max-w-[500px]">
          <Spinner />
        </div>
      )
    }

    if (eventTypesData && eventTypesData.eventTypes.length === 0) {
      return (
        <div className="booker-container">
          <AlertBanner variant="warning" title={t('no_event_types_found')}>
            {t('bookings_are_currently_unavailable_please_try_again_later')}
          </AlertBanner>
        </div>
      )
    }

    return (
      <Booker
        storeConfigData={storeConfigData}
        siteConnectionCount={availabilityData?.connectionsFound || 0}
        availabilitySlots={availabilityData?.timeSlots}
        selectedEventType={eventTypeData?.eventType}
        eventTypes={eventTypesData?.eventTypes}
        eventTypeSchedules={eventTypesData?.eventTypeSchedules}
        members={membersData?.members}
        groupClass={attendanceId ? attendanceData?.groupClass : undefined}
        groupClassAttendance={
          attendanceId ? attendanceData?.attendance : undefined
        }
        groupClassAttendanceContact={
          attendanceId ? attendanceData?.contact : undefined
        }
        groupClassEventType={attendanceData?.eventType}
        slotsByDay={scheduleData?.slotsByDay}
        siteSlug={siteSlug}
        booking={bookingId ? bookingData?.booking : undefined}
        bookingItems={bookingId ? bookingData?.items : undefined}
        source={source}
        onRootBack={rootBackCallback}
        isLoading={availabilityIsLoading}
        stripeConnectedAccountId={
          storeConfigData?.stripeLinkedAccount?.stripeAccountId
        }
        currentUser={userData?.userProfile}
        isEmbedMode={isEmbedMode}
      />
    )
  },
)
